import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGame, createGame } from '../api/gameApi';
import './Summary.css';

function Summary() {
  const navigate = useNavigate();
  const [gameData, setGameData] = useState(null);
  const [scores, setScores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gameId = params.get('id');
    const scoresParam = params.get('scores');

    if (gameId && scoresParam) {
      loadGameSummary(gameId, scoresParam);
    } else {
      setError('Missing game data');
      setIsLoading(false);
    }

  }, []);

  async function loadGameSummary(gameId, scoresParam) {
    try {
      const data = await getGame(gameId);
      const parsedScores = JSON.parse(decodeURIComponent(scoresParam));

      setGameData(data);
      setScores(parsedScores);
      setIsLoading(false);
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  }

  async function handlePlayAgain() {
    try {
      const data = await createGame();
      navigate(`/game?id=${data.gameId}`);
    } catch (err) {
      alert('Error creating game: ' + err.message);
    }
  }

  function handleShareGame() {
    const shareUrl = `${window.location.origin}/game?id=${gameData.gameId}`;
    if (navigator.share) {
      navigator.share({
        title: 'PhenoGuessr Game',
        text: `I scored ${totalScore} points! Can you beat my score?`,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Game link copied to clipboard!');
    }
  }

  if (isLoading) {
    return <div className="loading">Loading results...</div>;
  }

  if (error || !gameData) {
    return <div className="error">Error: {error || 'No game data'}</div>;
  }

  const totalScore = scores.reduce((sum, s) => sum + s.score, 0);
  const averageDistance = Math.round(
    scores.reduce((sum, s) => sum + s.distance, 0) / scores.length
  );

  return (
    <div className="summary-container">
      <div className="summary-content">
        <div className="summary-header">
          <h1>🎉 Game Complete!</h1>
          <div className="total-score">
            <div className="score-value">{totalScore}</div>
            <div className="score-label">Total Score</div>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{scores.length}</div>
            <div className="stat-label">Rounds Played</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{averageDistance} km</div>
            <div className="stat-label">Avg Distance</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{Math.round(totalScore / scores.length)}</div>
            <div className="stat-label">Avg Score</div>
          </div>
        </div>

        <div className="rounds-breakdown">
          <h2>Round Breakdown</h2>
          {scores.map((round, index) => (
            <div key={index} className="round-item">
              <div className="round-header">
                <span className="round-number">Round {index + 1}</span>
                <span className="round-score">{round.score} pts</span>
              </div>
              <div className="round-details">
                <div className="round-images">
                  <img
                    src={`/assets/images/${gameData.phenotypes[index].id}_male.jpg`}
                    alt="Male"
                  />
                  <img
                    src={`/assets/images/${gameData.phenotypes[index].id}_female.jpg`}
                    alt="Female"
                  />
                </div>
                <div className="round-stats">
                  <div>Distance: {round.distance} km</div>
                  <div>Phenotype: {gameData.phenotypes[index].name}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="action-buttons">
          <button className="primary-button" onClick={handlePlayAgain}>
            Play Again
          </button>
          <button className="secondary-button" onClick={handleShareGame}>
            Share Game
          </button>
          <button className="tertiary-button" onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

export default Summary;
